import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccount, useSignMessage } from 'wagmi'
import {
  fetchNonce,
  logout as apiLogout,
  refreshSession,
  setAccessToken,
  verifySignature,
} from '@/lib/api'
import { activeChain } from '@/lib/wagmi'

export interface Session {
  address: string
  isAdmin: boolean
  /** This wallet's own referral code. Null only for rows predating the feature. */
  referralCode?: string | null
  /**
   * True while referral attribution is unsettled — the first-time prompt is owed.
   *
   * Carried on the session rather than fetched separately so the modal can be decided
   * on the sign-in response itself, and re-derived on a silent restore where `verify`
   * never runs.
   */
  needsReferral?: boolean
}

/*
 * A React ref only protects one mounted hook. Keep the wire operation at module scope
 * too: a remount, duplicated React root, or delayed wallet connector update must not
 * create a second `personal_sign` prompt for the same wallet.
 */
let sharedSiweFlight: { key: string; promise: Promise<Session> } | null = null

/**
 * SIWE session management.
 *
 * The access token lives in memory only. On page load we attempt a silent refresh
 * against the httpOnly cookie — so a reload restores the session without asking the
 * user to sign again, while an XSS payload still cannot read the token.
 *
 * A refresh is scheduled at 80% of the token lifetime so requests never race an expiry.
 */
export function useAuth() {
  const { address, isConnected, isReconnecting, chainId } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const queryClient = useQueryClient()

  const [session, setSession] = useState<Session | null>(null)
  const [restoring, setRestoring] = useState(true)
  const [signingIn, setSigningIn] = useState(false)
  const [signInError, setSignInError] = useState<Error | null>(null)
  // Every wallet/session transition invalidates an older silent refresh. Without this,
  // an old cookie's late response can clear a brand-new SIWE session that completed
  // while the refresh was still in flight.
  const restoreSequence = useRef(0)
  /*
   * Auto sign-in, the header button and the protected-route prompt can all request
   * a sign-in during the same render. A mutation's `isPending` value updates only on
   * the next render, so it cannot make that operation single-flight. Keep the actual
   * promise in a ref instead: every caller receives the same nonce/signature/result.
   */
  const signInFlight = useRef<Promise<Session> | null>(null)

  // A session belongs to a wallet. Do not ask the API to restore one until wagmi has
  // finished reconnecting and there is a wallet to bind it to. Besides avoiding a
  // noisy expected 401 for signed-out visitors, this prevents a browser cookie from
  // briefly restoring the previous wallet's session before the current wallet is known.
  useEffect(() => {
    const restoreId = ++restoreSequence.current
    if (isReconnecting) return

    if (!isConnected || !address) {
      setAccessToken(null)
      setSession(null)
      setRestoring(false)
      return
    }

    let cancelled = false
    setRestoring(true)
    refreshSession()
      .then((result) => {
        if (cancelled || restoreSequence.current !== restoreId) return
        if (result.address.toLowerCase() !== address.toLowerCase()) {
          // Never attach Alice's persisted browser session to Bob's connected wallet.
          // Clear the old cookie and make the connected wallet complete its own SIWE
          // sign-in instead.
          setAccessToken(null)
          setSession(null)
          void apiLogout().catch(() => {})
          return
        }
        setAccessToken(result.accessToken)
        setSession({
          address: result.address,
          isAdmin: result.isAdmin,
          referralCode: result.referralCode ?? null,
          needsReferral: result.needsReferral ?? false,
        })
      })
      .catch(() => {
        // No valid cookie — the user simply isn't signed in yet.
      })
      .finally(() => {
        if (!cancelled && restoreSequence.current === restoreId) setRestoring(false)
      })

    return () => {
      cancelled = true
    }
  }, [address, isConnected, isReconnecting])

  // Proactive rotation at 12 minutes of a 15-minute token.
  useEffect(() => {
    if (!session) return
    const id = setInterval(
      () => {
        refreshSession()
          .then((result) => {
            setAccessToken(result.accessToken)
            /*
             * The rotation keeps whatever referral state the session already had when
             * the refresh response omits it — a token rotation is not new information
             * about attribution, and blanking it here would close an open prompt.
             */
            setSession((prev) => ({
              address: result.address,
              isAdmin: result.isAdmin,
              referralCode: result.referralCode ?? prev?.referralCode ?? null,
              needsReferral: result.needsReferral ?? prev?.needsReferral ?? false,
            }))
          })
          .catch(() => {
            setAccessToken(null)
            setSession(null)
          })
      },
      12 * 60 * 1000,
    )
    return () => clearInterval(id)
  }, [session])

  /**
   * The session is bound to one address. It must be torn down when that binding
   * breaks, which happens two ways:
   *
   *   • The user switches account in their wallet.
   *   • The user disconnects from the wallet's own UI (not our button).
   *
   * The second case was previously missed: the guard required `address` to be
   * truthy, so a disconnect — which sets `address` to undefined — fell straight
   * through and left the app rendering a signed-in header with no wallet behind it,
   * still holding a valid access token.
   *
   * `isReconnecting` is excluded deliberately. During rehydration the address is
   * momentarily undefined; treating that as a disconnect would log out every user
   * on every page load.
   */
  useEffect(() => {
    if (!session || isReconnecting) return

    const disconnected = !isConnected || !address
    const switchedAccount =
      !!address && address.toLowerCase() !== session.address.toLowerCase()

    if (disconnected || switchedAccount) {
      setAccessToken(null)
      setSession(null)
      // Best-effort cookie clear so the next visitor on this browser cannot
      // silently resume someone else's session via the refresh cookie.
      void apiLogout().catch(() => {})
      void queryClient.invalidateQueries()
    }
  }, [address, isConnected, isReconnecting, session, queryClient])

  const signIn = useCallback((): Promise<Session> => {
    if (signInFlight.current) return signInFlight.current

    // A manual/automatic SIWE attempt supersedes any old cookie restore immediately.
    // Do this before requesting a nonce, not after the signature, because the browser
    // can complete a refresh while the user is looking at their wallet prompt.
    restoreSequence.current += 1
    setRestoring(false)

    if (!address) {
      const failure = Promise.reject<Session>(new Error('Connect a wallet first.'))
      // The callers deliberately handle rejected sign-in attempts, but attaching a
      // handler here also avoids a transient unhandled-rejection report before React
      // receives the returned promise.
      void failure.catch(() => {})
      return failure
    }

    const signerAddress = address
    const signerChainId = chainId ?? activeChain.id
    const key = `${signerAddress.toLowerCase()}:${signerChainId}`
    let transport = sharedSiweFlight?.key === key ? sharedSiweFlight.promise : null

    if (!transport) {
      transport = (async () => {
      if (!address) throw new Error('Connect a wallet first.')

      const { nonce, domain, statement } = await fetchNonce()

      // EIP-4361 message, assembled to match exactly what the server verifies.
      const issuedAt = new Date().toISOString()
      const message = [
        `${domain} wants you to sign in with your Ethereum account:`,
        signerAddress,
        '',
        statement,
        '',
        `URI: ${window.location.origin}`,
        'Version: 1',
        /*
         * The chain the wallet is ACTUALLY on, not a hardcoded default.
         *
         * This read `activeChain.id` (BNB Chain), so a user signing while on
         * BNB testnet or Arbitrum produced an EIP-4361 message asserting chain 56.
         * The signature is valid either way, but the message is a false statement
         * about where the user is, and any server that checks the field rejects it.
         */
        `Chain ID: ${signerChainId}`,
        `Nonce: ${nonce}`,
        `Issued At: ${issuedAt}`,
      ].join('\n')

      const signature = await signMessageAsync({ message })
      const result = await verifySignature(message, signature)
      setAccessToken(result.accessToken)
      return {
        address: result.address,
        isAdmin: result.isAdmin,
        referralCode: result.referralCode,
        needsReferral: result.needsReferral,
      }
      })()
      sharedSiweFlight = { key, promise: transport }
    }

    const attempt = transport.then((nextSession) => {
      setSession(nextSession)
      void queryClient.invalidateQueries()
      return nextSession
    })

    // This assignment happens synchronously, before either nonce/network promise can
    // settle, so a second click cannot start another SIWE flow.
    signInFlight.current = attempt
    setSigningIn(true)
    setSignInError(null)

    void attempt
      .catch((error: unknown) => {
        setSignInError(error instanceof Error ? error : new Error('Sign-in failed.'))
      })
      .finally(() => {
        if (signInFlight.current === attempt) signInFlight.current = null
        if (sharedSiweFlight?.promise === transport) sharedSiweFlight = null
        setSigningIn(false)
      })

    return attempt
  }, [address, chainId, queryClient, signMessageAsync])

  /**
   * Clears the prompt flag once attribution is settled.
   *
   * Local rather than a refetch: the bind response already told us the outcome, and a
   * round trip here would leave the modal on screen for another RTT after the user had
   * visibly finished with it.
   */
  const markReferralSettled = useCallback(() => {
    setSession((prev) => (prev ? { ...prev, needsReferral: false } : prev))
  }, [])

  /*
   * Once a wallet is connected, offer SIWE immediately. The per-wallet marker makes
   * this one automatic attempt per tab, so reconnect/HMR cannot create duplicate
   * MetaMask prompts. Rejection/failure still leaves the explicit Sign in required
   * screen available for a deliberate retry.
   */
  const autoAttempted = useRef(new Set<string>())
  useEffect(() => {
    if (restoring || isReconnecting) return
    if (!isConnected || !address || session || signingIn || signInError) return

    const key = address.toLowerCase()
    const tabKey = `ninja:auto-siwe:v2:${key}`
    // Persist the one auto attempt across Vite HMR and ordinary reloads. Otherwise a
    // connected developer on a protected URL can spend the auth bucket just by saving
    // files, and a user who rejected once gets prompted again on refresh instead of
    // receiving the explicit retry control.
    if (autoAttempted.current.has(key) || sessionStorage.getItem(tabKey)) return
    autoAttempted.current.add(key)
    sessionStorage.setItem(tabKey, '1')
    void signIn().catch(() => {})
  }, [restoring, isReconnecting, isConnected, address, session, signIn, signInError, signingIn])

  useEffect(() => {
    if (isConnected) return

    // A rejected prompt should not keep reopening while this wallet remains
    // connected. But an explicit disconnect means the next connection is a new
    // sign-in opportunity, so remove the tab markers as well.
    // The ref is empty after an HMR reload, so also remove only our own known
    // marker namespace. This is not session/auth data — just the prompt dedupe flag.
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index)
      if (key?.startsWith('ninja:auto-siwe:v2:')) sessionStorage.removeItem(key)
    }
    autoAttempted.current.clear()
  }, [isConnected])

  const signOut = useCallback(async () => {
    await apiLogout().catch(() => setAccessToken(null))
    setSession(null)
    await queryClient.invalidateQueries()
  }, [queryClient])

  return {
    session,
    restoring,
    isSignedIn: session !== null,
    /**
     * Connected but not signed in. An OFFER, not a requirement — see the note on
     * `isSignedIn` in wallet-gate. Nothing on-chain may branch on this.
     */
    canSignIn: isConnected && session === null && !restoring,
    signIn,
    signingIn,
    signInError,
    signOut,
    /** True when the first-time referral prompt is still owed. */
    needsReferral: session?.needsReferral === true,
    referralCode: session?.referralCode ?? null,
    markReferralSettled,
  }
}

/**
 * Sign-in is optional, so its failures are informational.
 *
 * An unreachable API is by far the most common cause in development, and the
 * generic "Sign-in failed" sent people hunting through wallet settings for a
 * problem that was a server not running.
 */
export function signInFailureReason(error: Error): string {
  const m = error.message
  if (/user rejected|user denied|signature.*reject/i.test(m))
    return 'You cancelled the sign-in request. Your wallet is still connected — try again whenever you are ready.'
  if (/failed to fetch|networkerror|load failed/i.test(m))
    return 'Cannot reach the API — on-chain actions still work without signing in.'
  if (/expired|no longer valid|already used/i.test(m)) return 'Request expired — try again.'
  return 'Sign-in failed.'
}
